import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { adminApi } from '../../../api/adminApi';
import type { AssignmentSummary } from '../../../types/assignmentQuality';
import { assignmentPath } from './assignmentRoutes';
import './AssignmentQuality.css';

const DURATIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
  { value: 'all', label: 'All time' },
];

/** Far enough back to predate any grant; "all time" is still a bounded query. */
const ALL_TIME_FROM = '2000-01-01T00:00:00.000Z';

/** How long a typed search waits before it becomes a request. Long enough that
 *  a word costs one query rather than one per keystroke. */
const SEARCH_DEBOUNCE_MS = 350;

const PAGE_SIZE = 25;

/** Sortable columns → the value each sorts on. Client-side, because the whole
 *  window is fetched in one call and filtered in memory — there is no server
 *  page for a sort to disagree with. */
const SORT_VALUES: Record<string, (a: AssignmentSummary) => string | number> = {
  assigned: a => Date.parse(a.createdAt) || 0,
  title: a => a.title.toLowerCase(),
  assigner: a => (a.assignedByName || a.assignedById).toLowerCase(),
  students: a => a.students,
  questions: a => a.totalQuestions,
};

/** Settles `value` after `ms` of quiet. Keeps a server-side search from firing
 *  a query per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * S1 — every assignment in a window.
 *
 * A row is a grant GROUP: (question set, assigner, batch). The grants
 * themselves are per student, so "students" is the row's real size.
 * See docs/FeatureDesigns/Evals/assignment-analysis-quality.md §2.
 */
export function AssignmentsTab() {
  const navigate = useNavigate();
  // The window drives the QUERY; search and sort are applied to what it returns.
  const [filters, setFilters] = useState<Record<string, string>>({
    duration: '30', assignerId: '', search: '',
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'assigned', sortOrder: 'desc' });
  const [page, setPage] = useState(1);

  const duration = filters.duration || '30';
  // Debounced so the query key changes once per pause, not once per keystroke —
  // this search reaches Cosmos.
  const search = useDebounced(filters.search ?? '', SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isFetching, error } = useQuery({
    // Keyed on the filters, not on a timestamp computed during render — the
    // duration is what the user chose; "now" is resolved when the query runs.
    queryKey: ['evalAssignments', duration, filters.assignerId, search],
    queryFn: () => adminApi.listEvalAssignments({
      from: duration === 'all'
        ? ALL_TIME_FROM
        : new Date(Date.now() - Number(duration) * 86400_000).toISOString(),
      assignerId: filters.assignerId || undefined,
      search: search.trim() || undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const filtered = useMemo(() => data?.assignments ?? [], [data]);

  const sorted = useMemo(() => {
    const valueOf = SORT_VALUES[sort.sortBy];
    if (!valueOf) return filtered;
    const factor = sort.sortOrder === 'asc' ? 1 : -1;
    // Copy: react-query's cached array must not be reordered in place.
    return [...filtered].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === bv) return 0;
      return (typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))) * factor;
    });
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // A filter change can shrink the list under the current page; clamp rather
  // than show an empty page the user never navigated to.
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [sorted, safePage],
  );

  const filterDefs: FilterDef[] = [
    // `required`: the query always needs a lower bound, even for "all time".
    { key: 'duration', placeholder: 'Duration', options: DURATIONS, required: true },
    {
      key: 'assignerId',
      placeholder: 'Anyone',
      // Options come from the window, NOT from the rows on screen — deriving
      // them from filtered rows would offer only the assigner already picked.
      options: (data?.assigners ?? []).map(a => ({ value: a.id, label: a.name })),
    },
  ];

  const columns: ColumnDef<AssignmentSummary>[] = useMemo(() => [
    {
      key: 'assigned',
      header: 'Assigned',
      sortable: true,
      render: a => <span>{format(new Date(a.createdAt), 'MMM d, yyyy')}</span>,
    },
    {
      key: 'title',
      header: 'Assignment',
      sortable: true,
      mobile: 'title',
      render: a => (
        <div className="dc-stack">
          <span className="dc-name">{a.title}</span>
          <span className="aq-muted">{a.subject}{a.modality ? ` · ${a.modality}` : ''}</span>
        </div>
      ),
    },
    {
      key: 'assigner',
      header: 'Assigned by',
      sortable: true,
      render: a => (
        <div className="dc-stack">
          <span>{a.assignedByName || a.assignedById.slice(0, 12)}</span>
          {a.assignedByType && <span className="aq-muted">{a.assignedByType}</span>}
        </div>
      ),
    },
    {
      key: 'students',
      header: 'Students',
      sortable: true,
      render: a => (
        <span className="aq-metric">
          <b>{a.students}</b>
          {a.declined > 0 && <span className="aq-badge warn">{a.declined} declined</span>}
        </span>
      ),
    },
    {
      key: 'questions',
      header: 'Questions',
      sortable: true,
      render: a => <span>{a.totalQuestions}</span>,
    },
  ], []);

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <div className="aq-page">

      <DataTable
        toolbar={
          <SearchFilterBar
          filters={filterDefs}
          values={filters}
          onChange={next => { setFilters(next); setPage(1); }}
          searchPlaceholder="assignment name, subject, assigner"
          // Auto-apply: every control refetches on change (debounced for the
          // search box), so there is nothing for a Fetch button to commit.
          hideFetch
          />
        }
        columns={columns}
        data={pageRows}
        rowKey={a => `${a.questionSetId}|${a.assignedById}|${a.batchId ?? ''}`}
        onRowClick={a => navigate(assignmentPath(a))}
        rowHref={a => assignmentPath(a)}
        loading={isLoading || isFetching}
        emptyMessage="No assignments in this window."
        sort={sort}
        onSortChange={next => { setSort(next); setPage(1); }}
        pagination={{
          page: safePage,
          totalPages,
          total: sorted.length,
          onPageChange: setPage,
        }}
      />

      {data?.truncated && (
        <p className="aq-warn">
          Hit the grant cap ({data.scanned} rows) — older assignments in this window are missing.
          Narrow the window to see them.
        </p>
      )}
    </div>
  );
}
